import { Box, LinearProgress, Typography, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useContext } from 'react'
import RootStore from '../stores/RootStore'
import AnimatedBox from './Motion/AnimatedBox'
import { AnimatePresence } from 'motion/react'
import StyledIcon from './StyledIcon'
import { MaterialColor } from '../theme/default'
import zIndexes from '../theme/zIndexes'
import { CONTRAST_ALERT_TYPES, DropdownAlertType } from '../stores/DropdownMessageHandler'
import icons from '../assets/icons'

const DropDownMessage = () => {
  const store = useContext(RootStore)
  const theme = useTheme()

  const { visible, messageData, actionComponent, sx, hideDropdownMessage } = store.dropdownMessageHandler

  const iconsMap: Record<DropdownAlertType, keyof typeof icons> = {
    info: 'info',
    warn: 'warning',
    error: 'warning',
    success: 'check',
    'contrast-success': 'check',
    custom: 'info',
  }

  const icon8 = iconsMap[messageData.type]
  const isContrast = CONTRAST_ALERT_TYPES.includes(messageData.type)
  const paletteType = isContrast ? messageData.type.replace('contrast-', '') : messageData.type

  const bgColor = isContrast ? 'background.paper' : `${paletteType}.main`
  const borderColor = isContrast ? `${paletteType}.main` : `${paletteType}.dark`
  const textColor = isContrast ? 'text.primary' : `${paletteType}.contrastText`
  const iconColor = isContrast ? `${paletteType}.main` : `${paletteType}.contrastText`

  return <AnimatePresence>
    {visible &&
      <AnimatedBox
        sx={[
          {
            bgcolor: bgColor,
            position: 'fixed',
            bottom: theme.spacing(3),
            right: theme.spacing(3),
            borderRadius: theme.spacing(1),
            pl: 3,
            py: 2,
            pr: 4,
            zIndex: zIndexes.popover,
            border: isContrast ? `1px solid ${theme.palette.divider}` : `2px solid ${borderColor}`,
            color: textColor,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
        entering={['fade', 'right']}
        exiting={['fade', 'right']}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <StyledIcon
            name={icon8}
            size={42}
            color={iconColor as MaterialColor}
            sx={{
              mr: -1,
              ml: -1,
            }}
          />

          <Box>
            {messageData.title && <Typography color='inherit' variant='body1' fontWeight={'medium'}>{messageData.title}</Typography>}
            <Typography color='inherit'>{messageData.message}</Typography>
            {actionComponent}
          </Box>
        </Box>

        {messageData.showProgressAnimation && (
          <LinearProgress
            variant='determinate'
            value={100}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              borderRadius: `${theme.spacing(1)} ${theme.spacing(1)} 0 0`,
              height: 4,
              bgcolor: 'transparent',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
                animation: `shrinkProgress ${messageData.closeInterval}ms linear forwards`,
                transformOrigin: 'left',
              },
              '@keyframes shrinkProgress': {
                from: { transform: 'scaleX(1)' },
                to: { transform: 'scaleX(0)' },
              },
            }}
          />
        )}

        <Box
          onClick={hideDropdownMessage}
          sx={{
            opacity: 0.4,
            cursor: 'pointer',
            position: 'absolute',
            top: theme.spacing(1),
            right: theme.spacing(1),
          }}
        >
          <StyledIcon name='smCross' size={20} color={textColor as MaterialColor} />
        </Box>
      </AnimatedBox>
    }
  </AnimatePresence>
}

export default observer(DropDownMessage)